import React, { useCallback, memo, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { Link } from 'react-router-dom'
import {useRedux} from "../../../hooks";
import {Label, Row,} from 'reactstrap';

import InputText from "../../../components/InputText";
import InputPassword from "../../../components/InputPassword";

import '../../../i18/config';
import {useTranslation} from "react-i18next";

import {
    Formik,
    Form,
    Field
} from 'formik';
import {MainUserSignUpType} from "../../../redux/Main/User/types";
import {NewMainUserSignUp} from "../../../redux/Main/User/reducer";
import {SignUpFormValidatorSchema} from "./SignUpFormValidator";
import {onSendSignUpUser, setSignUpStatus} from "../../../redux/Main/User/actions";
import {getCurrentLanguage, getParentPartner} from "../../../helpers/functions";
import {browserSupportsWebAuthn} from "@simplewebauthn/browser";
import * as route from "../../../api/routes";
import { GET_CSRF_TOKEN } from "../../../api/urls";
import { APIClient, setCSRFToken } from "../../../api/apiCore";

const SignUpForm = memo(() => {
    const { t } = useTranslation(['user','validator']);
    const { dispatch, useAppSelector } = useRedux();

    const isBrowserSupportsWebAuthn = browserSupportsWebAuthn();

    const { isSendSignUp } = useAppSelector(state => ({
        isSendSignUp: state.MainUser.isSendSignUp,
    }));

    useEffect(() => {
        const api = new APIClient();
        api.get(GET_CSRF_TOKEN).then((res: any) => {
            if (res && res.csrf_token) {
                setCSRFToken(res.csrf_token);
            }
        }).catch(() => {});
    }, []);

    const onDataSubmit = useCallback((formData: MainUserSignUpType) => {
        dispatch(setSignUpStatus(true));

        let params: MainUserSignUpType = {
            email: formData.email,
            password: formData.password,
            language: getCurrentLanguage()
        };

        const parentPartnerMark = getParentPartner();
        if (parentPartnerMark?.partnerCode && parentPartnerMark?.partnerCode.length > 0) {
            params = {
                ...params,
                partnerCode: parentPartnerMark.partnerCode
            };
        }

        dispatch(onSendSignUpUser(params));
    }, [dispatch]);

    return (
        <>
        <Formik
            initialValues={NewMainUserSignUp}
            validationSchema={SignUpFormValidatorSchema}
            onSubmit={(values, actions) => {
                onDataSubmit(values);
                actions.setSubmitting(false);
            }}
            enableReinitialize={false}
        >
            {({ errors, touched, setFieldValue, getFieldProps }) => (
                <Form noValidate={true} className="customValidation">
                    <Row>
                        <div className={"mb-3" + (touched.email ? " was-validated" : '')}>
                            <Label className="form-label" htmlFor="form-email-input">E-mail</Label>
                            <Field
                                id="form-email-input"
                                name="email"
                                placeholder={t('user:enterEmail')}
                                component={InputText}
                                value={getFieldProps('email').value}
                                onChange={(value: string) => {
                                    setFieldValue('email', value);
                                }}
                                className={errors.email && touched.email && 'form-control is-invalid'}
                            />
                            {errors.email && touched.email ? (
                                <div className="invalid-feedback">
                                    {t('validator:' + errors.email)}
                                </div>
                            ) : touched.email && !errors.email ?
                                <div className="valid-feedback">
                                    {t('common:looksGood')}
                                </div>
                                : ''
                            }
                        </div>
                    </Row>
                    <Row>
                        <div className={"mb-3" + (touched.password ? " was-validated" : '')}>
                            <Label className="form-label" htmlFor="form-password-input">{t('user:password')}</Label>
                            <Field
                                id="form-password-input"
                                name="password"
                                placeholder={t('user:enterPassword')}
                                component={InputPassword}
                                value={getFieldProps('password').value}
                                onChange={(value: string) => {
                                    setFieldValue('password', value);
                                }}
                                className={errors.password && touched.password && 'form-control is-invalid'}
                            />
                            {(errors.password && touched.password) ? (
                                <div className="invalid-feedback">
                                    {t('validator:' + errors.password)}
                                </div>
                            ) : (touched.password && !errors.password) ?
                                <div className="valid-feedback">
                                    {t('common:looksGood')}
                                </div>
                                : ''
                            }
                        </div>
                    </Row>
                    <Row>
                        <div className={"mb-3" + (touched.confirmPassword ? " was-validated" : '')}>
                            <Label className="form-label" htmlFor="form-confirmpassword-input">{t('user:confirmPassword')}</Label>
                            <Field
                                id="form-confirmpassword-input"
                                name="confirmPassword"
                                placeholder={t('user:enterConfirmPassword')}
                                component={InputPassword}
                                value={getFieldProps('confirmPassword').value}
                                onChange={(value: string) => {
                                    setFieldValue('confirmPassword', value);
                                }}
                                className={errors.confirmPassword && touched.confirmPassword && 'form-control is-invalid'}
                            />
                            {errors.confirmPassword && touched.confirmPassword ? (
                                <div className="invalid-feedback">
                                    {t('validator:' + errors.confirmPassword)}
                                </div>
                            ) : touched.confirmPassword && !errors.confirmPassword ?
                                <div className="valid-feedback">
                                    {t('common:looksGood')}
                                </div>
                                : ''
                            }
                        </div>
                    </Row>

                    <div className="mb-4">
                        <p className="mb-0 fs-12 text-muted fst-italic">{t('auth:byRegisteringYouAgree')} <a href="https://marketcells.io/license" className="text-primary text-decoration-underline fst-normal fw-medium">{t('auth:termsOfUse')}</a></p>
                    </div>

                    <div className="mt-4">
                        <Button
                            type="submit"
                            disabled={isSendSignUp}
                            color="success"
                            className="w-100 btn-success mb-2">{t('auth:signUp')}</Button>
                    </div>
                </Form>
            )}
        </Formik>
            { isBrowserSupportsWebAuthn &&
                <Link to={route.MAIN_AUTH_SIGN_UP_PASSKEY}
                    className="btn btn-outline-primary w-100">{t('auth:registerWithPasskey')}
                </Link>
            }
        </>
    );
});

export default SignUpForm;